import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import StrataMap from '@/components/StrataMap';

const CLARITYOS_RECORD_URL = 'https://clarity-os.co/record';

// Bright state colours - exact hex values
const STATE_COLORS = {
  ground: '#4CAF82',
  observe: '#4A90C4',
  connect: '#B06BB0',
  execute: '#D4723A',
  integrate: '#7BAF4A'
};

// Feature card icons as lotus-petal derived SVGs
const FeatureIcon = ({ type, size = 40 }) => {
  const icons = {
    ownership: (
      // Single upward teardrop petal - Ground
      <svg width={size} height={size * 1.3} viewBox="0 0 40 52">
        <defs>
          <linearGradient id="ground-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={STATE_COLORS.ground} stopOpacity="0.9" />
            <stop offset="100%" stopColor={STATE_COLORS.ground} stopOpacity="0.6" />
          </linearGradient>
        </defs>
        <path
          d="M20 2 C8 18, 4 28, 4 36 C4 46, 11 50, 20 50 C29 50, 36 46, 36 36 C36 28, 32 18, 20 2"
          fill="url(#ground-grad)"
        />
      </svg>
    ),
    growth: (
      // Three overlapping petal layers - Integrate
      <svg width={size} height={size * 1.3} viewBox="0 0 40 52">
        <defs>
          <linearGradient id="integrate-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={STATE_COLORS.integrate} stopOpacity="0.9" />
            <stop offset="100%" stopColor={STATE_COLORS.integrate} stopOpacity="0.5" />
          </linearGradient>
        </defs>
        <path d="M20 10 C14 20, 12 26, 12 32 C12 38, 15 42, 20 42 C25 42, 28 38, 28 32 C28 26, 26 20, 20 10" fill={STATE_COLORS.integrate} fillOpacity="0.3" />
        <path d="M20 6 C12 18, 8 26, 8 34 C8 42, 13 46, 20 46 C27 46, 32 42, 32 34 C32 26, 28 18, 20 6" fill={STATE_COLORS.integrate} fillOpacity="0.5" />
        <path d="M20 2 C10 16, 6 26, 6 36 C6 46, 12 50, 20 50 C28 50, 34 46, 34 36 C34 26, 30 16, 20 2" fill="url(#integrate-grad)" />
      </svg>
    ),
    observer: (
      // Four petals toward shared centre - Connect
      <svg width={size} height={size * 1.3} viewBox="0 0 40 52">
        <g transform="translate(20, 26)">
          <path d="M0 -20 C-4 -12, -4 -6, 0 0 C4 -6, 4 -12, 0 -20" fill={STATE_COLORS.connect} fillOpacity="0.8" />
          <path d="M20 0 C12 -4, 6 -4, 0 0 C6 4, 12 4, 20 0" fill={STATE_COLORS.connect} fillOpacity="0.7" />
          <path d="M0 20 C4 12, 4 6, 0 0 C-4 6, -4 12, 0 20" fill={STATE_COLORS.connect} fillOpacity="0.6" />
          <path d="M-20 0 C-12 4, -6 4, 0 0 C-6 -4, -12 -4, -20 0" fill={STATE_COLORS.connect} fillOpacity="0.5" />
        </g>
        <circle cx="20" cy="26" r="4" fill={STATE_COLORS.connect} />
      </svg>
    ),
    synthesis: (
      // Amber seed core with five petals at 60% opacity - Observe
      <svg width={size} height={size * 1.3} viewBox="0 0 40 52">
        <g transform="translate(20, 26)">
          {[0, 72, 144, 216, 288].map((angle, i) => (
            <path
              key={i}
              d="M0 -18 C-3 -10, -3 -5, 0 0 C3 -5, 3 -10, 0 -18"
              fill={STATE_COLORS.observe}
              fillOpacity="0.6"
              transform={`rotate(${angle})`}
            />
          ))}
        </g>
        <circle cx="20" cy="26" r="6" fill="#C8860A" />
      </svg>
    )
  };
  return icons[type] || null;
};

const features = [
  {
    iconType: 'ownership',
    title: 'Individual Ownership',
    description: 'Your leadership data belongs to you. Take it anywhere, share it with anyone, delete it anytime.',
    bgColor: STATE_COLORS.ground
  },
  {
    iconType: 'growth',
    title: 'Growth Trajectory',
    description: 'Track your leadership evolution over time. See patterns emerge across assessments and feedback.',
    bgColor: STATE_COLORS.integrate
  },
  {
    iconType: 'observer',
    title: 'Multi-Observer',
    description: 'Integrate psychometrics, 360 feedback, performance reviews, and personal reflections.',
    bgColor: STATE_COLORS.connect
  },
  {
    iconType: 'synthesis',
    title: 'AI-Powered Synthesis',
    description: 'Advanced AI creates unified insights from diverse assessment data.',
    bgColor: STATE_COLORS.observe
  }
];

export default function Landing() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    window.location.href = CLARITYOS_RECORD_URL;
  };

  useEffect(() => {
    if (!loading && user) {
      // Redirect existing users to ClarityOS
      window.location.href = CLARITYOS_RECORD_URL;
    }
  }, [user, loading]);

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/10 via-transparent to-transparent" />
        
        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-6 py-6 lg:px-12">
          <div className="flex items-center gap-3">
            <img 
              src="/strata-lotus-mark.svg" 
              alt="Strata Record" 
              className="w-10 h-10"
            />
            <span className="font-display text-2xl text-white">Strata Record</span>
          </div>
          <Button 
            onClick={login}
            className="strata-button-primary"
            data-testid="login-btn"
          >
            Sign In
          </Button>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 mx-auto max-w-7xl px-6 pt-16 pb-24 lg:px-12 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left column - Text */}
            <div className="animate-slide-up">
              <p className="font-mono text-amber-500 text-sm tracking-wider mb-4">
                THE LEADERSHIP RECORD
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Every layer of who you are as a leader
              </h1>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl">
                Strata Record unifies your entire leadership journey — psychometric assessments, 360 feedback, 
                performance reviews, and personal reflections — into a single, portable record that you own.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleGetStarted}
                  size="lg"
                  className="text-lg px-8 py-6 text-white hover:opacity-90"
                  style={{ backgroundColor: '#C8860A' }}
                  data-testid="get-started-btn"
                >
                  Begin Your Strata Record
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="mt-12 pt-8 border-t border-slate-mid/50">
                <p className="text-sm text-slate-500 mb-4">Built on the Strata Leadership Framework</p>
                <div className="flex flex-wrap gap-6">
                  {[
                    { name: 'Ground', color: '#4CAF82' },
                    { name: 'Observe', color: '#4A90C4' },
                    { name: 'Connect', color: '#B06BB0' },
                    { name: 'Execute', color: '#D4723A' },
                    { name: 'Integrate', color: '#7BAF4A' }
                  ].map(({ name, color }) => (
                    <div key={name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm text-slate-400">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column - Map visualization */}
            <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Glow effect */}
                <div className="absolute inset-0 blur-3xl opacity-30">
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-amber-600/50 via-state-connect/30 to-state-observe/30" />
                </div>
                
                {/* Map container */}
                <div className="relative strata-card p-8 flex justify-center">
                  <StrataMap 
                    scores={{ ground: 45, observe: 45, connect: 45, execute: 45, integrate: 45 }} 
                    size={380}
                    animated={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate/20 to-transparent" />
        
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">
              A New Kind of Leadership Record
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Unlike traditional assessments owned by organizations, Strata Record gives you permanent, 
              portable ownership of your complete leadership history.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={feature.title}
                className="strata-card p-6 transition-transform duration-500 hover:-translate-y-1"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div 
                  className="w-14 h-16 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${feature.bgColor}15` }}
                >
                  <FeatureIcon type={feature.iconType} size={40} />
                </div>
                <h3 className="font-display text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-12 text-center">
          <div className="strata-card-accent p-12 lg:p-16">
            <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">
              Your Strata Record belongs to you
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              We store it, but we do not own it. You can take it, share it, or delete it — always on your terms.
            </p>
            <Button 
              onClick={handleGetStarted}
              size="lg"
              className="text-lg px-8 py-6"
              style={{ backgroundColor: '#C8860A' }}
              data-testid="cta-start-btn"
            >
              Start Building Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-mid/50 py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/strata-lotus-mark.svg" 
                alt="Strata Record" 
                className="w-8 h-8"
              />
              <span className="font-display text-lg text-white">Strata Record</span>
            </div>
            <p 
              className="font-mono"
              style={{ 
                fontSize: '10px', 
                letterSpacing: '0.08em', 
                color: '#9BA8BE' 
              }}
            >
              © 2026 Strata Record — A product of Shim Partners Management Consultancy FZCO, UAE
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
